import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Trash2,
  Package,
  Search,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { useForm } from "react-hook-form";
import { createHttpClient } from "../../api/client";
import config from "../../config/env";
import { ReactComponent as Devices } from "../../assets/images/Devices.svg";
import { ReactComponent as Card } from "../../assets/images/Card.svg";
import { ReactComponent as Box } from "../../assets/images/Box.svg";
import { ReactComponent as ArrowRight } from "../../assets/images/ArrowRight.svg";

function classNames(...xs) {
  return xs.filter(Boolean).join(" ");
}

function SectionCard({ icon: Icon, title, right, children }) {
  return (
    <div id={typeof title === "string" ? title : undefined} className="rounded-xl bg-white">
      <div className="flex items-center justify-between gap-3 px-6 py-2">
        <div className="flex items-center gap-2 mt-2">
          {Icon ? <Icon size={16} className="text-slate-700" /> : null}
          <div className="text-sm font-semibold text-[#047DED]">{title}</div>
        </div>
        {right}
      </div>
      <div className="px-6 pb-2">{children}</div>
    </div>
  );
}

function UnderlineInput({
  name,
  label,
  value,
  onChange,
  placeholder = "กรอกข้อมูล",
  type = "text",
  readOnly = false,
  maxLength,
  rightText,
  forceUppercase = false,
  forceAlphanumeric = false,
}) {
  return (
    <div className="py-3">
      <div className="flex items-center justify-between">
        <div className="text-sm text-[#1D1616B2] font-semibold">{label}</div>
        {rightText ? <div className="text-xs text-slate-400">{rightText}</div> : null}
      </div>
      <input
        name={name}
        type={type}
        value={value ?? ""}
        maxLength={maxLength}
        autoCapitalize={forceUppercase || forceAlphanumeric ? "characters" : "off"}
        onChange={(e) => {
          const nextRaw = e.target.value;
          let next = String(nextRaw || "");
          if (forceUppercase || forceAlphanumeric) next = next.toUpperCase();
          if (forceAlphanumeric) next = next.replace(/[^A-Z0-9]/g, "");
          onChange?.(next);
        }}
        placeholder={placeholder}
        readOnly={readOnly}
        className={classNames(
          "mt-1 w-full bg-transparent text-sm text-slate-[#1D1616] outline-none placeholder:text-[#1D161680]",
          "border-b border-[#00000026]",
          (forceUppercase || forceAlphanumeric) && "uppercase",
          readOnly && "text-slate-700"
        )}
      />
    </div>
  );
}

function UnderlineSelect({
  name,
  label,
  value,
  onChange,
  options = [],
  placeholder = "เลือกข้อมูล",
}) {
  return (
    <div className="py-3">
      <div className="text-sm text-[#1D1616B2] font-semibold">{label}</div>
      <select
        name={name}
        value={value ?? ""}
        onChange={(e) => onChange?.(e.target.value)}
        className={classNames(
          "mt-1 w-full bg-transparent text-sm outline-none text-[#1D161680]",
          "border-b border-[#00000026]"
        )}
      >
        <option value="" disabled className="text-slate-500">
          {placeholder}
        </option>
        {options.map((o) => (
          <option key={o.value} value={o.value} className="text-slate-500">
            {o.label}
          </option>
        ))}
      </select>
    </div>
  );
}

function Money(input) {
  const value =
    input && typeof input === "object" && !Array.isArray(input)
      ? input.value
      : input;
  const n = Number(value || 0);
  return `฿${n.toLocaleString("th-TH")}`;
}

const PRODUCT_TYPES = [
  { value: "phone", label: "โทรศัพท์" },
  { value: "accessory", label: "อุปกรณ์เสริม" },
];

function asText(v) {
  if (v == null) return "";
  if (typeof v === "string" || typeof v === "number" || typeof v === "boolean") return String(v);
  if (Array.isArray(v)) return v.map(asText).filter(Boolean).join(", ");
  if (typeof v === "object") return v.name ?? v.label ?? v.title ?? v.value ?? (v.id != null ? String(v.id) : "");
  return String(v);
}

function normalizeStatus(v) {
  return String(v || "").trim().toLowerCase();
}

function formatWarrantyDisplay(info, expireDate) {
  const warranty = String(info || "").trim();
  if (warranty === "ประกันเหลือ") {
    if (!expireDate) return "ประกันเหลือ";
    const d = new Date(expireDate);
    if (Number.isNaN(d.getTime())) return "ประกันเหลือ";
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const yyyy = d.getFullYear();
    return `ประกันเหลือ (${dd}/${mm}/${yyyy})`;
  }
  if (warranty === "ประกันหมด") return "ประกันหมด";
  if (warranty.includes("ประกันศูนย์")) return "ประกันศูนย์ 1 ปี";
  return "-";
}

function formatDateDisplay(dateValue) {
  if (!dateValue) return "-";
  const d = new Date(dateValue);
  if (Number.isNaN(d.getTime())) return "-";
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
}

function mapPhoneOption(raw) {
  const productName = asText(raw?.product_name).trim();
  const categoryName = asText(raw?.model?.category?.name || raw?.category?.name);
  const modelName = asText(raw?.model?.name || raw?.model);
  const name =
    productName ||
    (categoryName && modelName ? `${categoryName} ${modelName}` : categoryName || modelName) ||
    asText(raw?.name) ||
    "-";
  const capacity = asText(raw?.capacity?.size || raw?.capacity || raw?.storage) || "-";
  const color = asText(raw?.color?.name || raw?.color) || "-";
  const warrantyInfo = asText(raw?.warranty_info || raw?.warranty_status || raw?.warranty) || "-";
  const warrantyDate = raw?.warranty_date || raw?.warranty_end_date || "";
  const warranty = formatWarrantyDisplay(warrantyInfo, warrantyDate);
  const batteryRaw = raw?.battery_health ?? raw?.batteryMax ?? raw?.battery;
  const batteryMax = batteryRaw == null || batteryRaw === "" ? "-" : String(batteryRaw);
  const price = Number(raw?.sell_price ?? 0) || 0;
  const imei = asText(raw?.imei || raw?.IMEI || raw?.serial) || "-";

  return {
    optionId: String(raw?.id ?? raw?.documentId ?? ""),
    productType: "phone",
    name,
    cap: capacity,
    color,
    warranty,
    warrantyInfo,
    warrantyDate,
    batteryMax,
    price,
    imei,
    raw,
  };
}

function mapAccessoryOption(raw) {
  const name = asText(raw?.name) || "-";
  const category = asText(raw?.accessory_category?.name || raw?.accessory_category) || "-";
  const price = Number(raw?.price ?? 0) || 0;
  return {
    optionId: String(raw?.id ?? raw?.documentId ?? ""),
    productType: "accessory",
    name,
    category,
    cap: "-",
    color: "-",
    warranty: "-",
    batteryMax: "-",
    price,
    imei: "-",
    raw,
  };
}

function ProductPickerPanel({ type, open, loading, query, rows, onToggle, onQueryChange, onSelect }) {
  if (!type) return null;
  const isPhone = type === "phone";

  return (
    <div className="mt-2 rounded-xl border border-slate-200 bg-[#F3F4F6]">
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-center justify-between px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-100 rounded-t-xl"
      >
        <span>เลือกรายการจากสต๊อก</span>
        {open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
      </button>

      {open && (
        <div className="space-y-3 border-t border-slate-200 p-3">
          <div className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2">
            <Search size={16} className="text-slate-400" />
            <input
              value={query}
              onChange={(e) => onQueryChange?.(e.target.value)}
              placeholder="ค้นหารายการ..."
              className="w-full bg-transparent text-sm outline-none placeholder:text-slate-400"
            />
          </div>

          <div className="overflow-auto rounded-xl border border-slate-200 bg-white">
            <table className="min-w-full text-sm">
              <thead className="bg-slate-50 text-slate-500">
                <tr>
                  <th className="px-3 py-2 text-left font-semibold">ชื่อสินค้า</th>
                  {isPhone ? (
                    <>
                      <th className="px-3 py-2 text-left font-semibold">สี</th>
                      <th className="px-3 py-2 text-left font-semibold">แบตเตอรี่</th>
                      <th className="px-3 py-2 text-left font-semibold">IMEI</th>
                    </>
                  ) : (
                    <th className="px-3 py-2 text-left font-semibold">ประเภท</th>
                  )}
                  <th className="px-3 py-2 text-left font-semibold">ราคา</th>
                  <th className="px-3 py-2 text-right font-semibold">เลือก</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={isPhone ? 6 : 4} className="px-3 py-4 text-center text-slate-400">
                      กำลังโหลดข้อมูล...
                    </td>
                  </tr>
                ) : rows.length === 0 ? (
                  <tr>
                    <td colSpan={isPhone ? 6 : 4} className="px-3 py-4 text-center text-slate-400">
                      ไม่พบรายการ
                    </td>
                  </tr>
                ) : (
                  rows.map((r) => (
                    <tr key={r.optionId} className="border-t border-slate-100">
                      <td className="px-3 py-2 text-slate-700">{r.name || "-"}</td>
                      {isPhone ? (
                        <>
                          <td className="px-3 py-2 text-slate-700">{r.color || "-"}</td>
                          <td className="px-3 py-2 text-slate-700">{r.batteryMax === "-" ? "-" : `${r.batteryMax}%`}</td>
                          <td className="px-3 py-2 text-slate-700">{r.imei || "-"}</td>
                        </>
                      ) : (
                        <td className="px-3 py-2 text-slate-700">{r.category || "-"}</td>
                      )}
                      <td className="px-3 py-2 text-slate-700">{Money(r.price)}</td>
                      <td className="px-3 py-2 text-right">
                        <button
                          type="button"
                          onClick={() => onSelect?.(r)}
                          className="text-sm font-semibold text-[#047DED] hover:underline"
                        >
                          เลือก
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------- page ----------------
const makeEmptyItem = (idx) => ({
  id: `${Date.now()}_${Math.random().toString(16).slice(2)}_${idx}`,
  productType: "",
  productId: "",
  category: "",
  imei: "",
  pickerOpen: false,
  pickerQuery: "",
  // cache display
  name: "",
  cap: "",
  color: "",
  warranty: "",
  warrantyInfo: "",
  warrantyDate: "",
  batteryMax: "",
  price: 0,
});

function toNumber(v) {
  const raw = String(v ?? "").replace(/,/g, "").trim();
  const m = raw.match(/-?\d+(?:\.\d+)?/);
  const n = m ? Number(m[0]) : NaN;
  return Number.isFinite(n) ? n : 0;
}

function normalizeUploadUrl(url) {
  if (!url) return "";
  if (/^https?:\/\//i.test(url)) return url;
  const base = String(config?.apiImg || "").replace(/\/+$/, "");
  if (!base) return url;
  if (url.startsWith("/")) return `${base}${url}`;
  return `${base}/${url}`;
}

function extractSellReportPdfUrlFromResponse(rawResponse) {
  const payload = rawResponse?.data ?? rawResponse;
  const data = payload?.data ?? payload;

  return normalizeUploadUrl(
    data?.pdf_path ||
      payload?.pdf_path ||
      data?.report_pdf_path ||
      data?.product?.report_pdf_path ||
      payload?.report_pdf_path ||
      ""
  );
}

function extractProductReportPdfUrlFromResponse(rawResponse) {
  const payload = rawResponse?.data ?? rawResponse;
  const entity = payload?.data?.attributes
    ? { id: payload.data.id, ...payload.data.attributes }
    : payload?.data
      ? payload.data
      : payload;

  return normalizeUploadUrl(
    entity?.report_pdf_path ||
      payload?.report_pdf_path ||
      payload?.data?.report_pdf_path ||
      payload?.data?.attributes?.report_pdf_path ||
      ""
  );
}

function delay(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

async function fetchProductReportPdfUrlById(http, idOrDocumentId) {
  if (!idOrDocumentId) return "";
  try {
    const res = await http.get(`/products/${idOrDocumentId}`);
    return extractProductReportPdfUrlFromResponse(res?.data);
  } catch (e) {
    const ref = String(idOrDocumentId || "").trim();
    const isNumeric = /^\d+$/.test(ref);

    if (!ref || isNumeric) {
      console.error(`load product pdf path error id=${idOrDocumentId}:`, e);
      return "";
    }

    try {
      const res = await http.get(`/products`, {
        params: {
          "filters[documentId][$eq]": ref,
          "pagination[page]": 1,
          "pagination[pageSize]": 1,
        },
      });
      const first = Array.isArray(res?.data?.data) ? res.data.data[0] : null;
      return extractProductReportPdfUrlFromResponse({ data: first || null });
    } catch (innerErr) {
      console.error(`load product pdf path error documentId=${idOrDocumentId}:`, innerErr);
      return "";
    }
  }
}

function ReportPreviewModal({ open, url, onClose }) {
  if (!open) return null;

  return (
    <>
      <div className="fixed inset-0 z-[520] bg-black/60" onClick={onClose} />
      <div className="fixed inset-0 z-[521] flex items-center justify-center p-4">
        <div className="w-full max-w-5xl h-[88vh] rounded-2xl bg-white shadow-2xl overflow-hidden flex flex-col">
          <div className="px-5 py-4 border-b border-black/10 flex items-center justify-between">
            <div className="text-base font-bold text-slate-900">ใบขายสินค้า</div>
            <button
              type="button"
              onClick={onClose}
              className="h-9 rounded-xl border border-slate-200 bg-white px-4 text-sm font-bold hover:bg-slate-50"
            >
              ปิด
            </button>
          </div>
          <div className="flex-1 bg-slate-100 p-4">
            {url ? (
              <div className="h-full w-full rounded-xl overflow-hidden border border-slate-200 bg-white">
                <iframe title="sellout-report-pdf" src={url} className="h-[calc(100%-56px)] w-full border-0" />
                <div className="h-14 px-4 border-t border-slate-200 flex items-center justify-between gap-3">
                  <div className="text-xs text-slate-500 break-all">{url}</div>
                  <a
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="shrink-0 inline-flex h-9 items-center rounded-lg bg-[#047DED] px-3 text-xs font-bold text-white hover:opacity-95"
                  >
                    เปิดแท็บใหม่
                  </a>
                </div>
              </div>
            ) : (
              <div className="h-full w-full flex items-center justify-center text-sm text-slate-500">
                ไม่พบไฟล์ใบขายสินค้า
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

function resolvePaymentMethod(values) {
  const cash = toNumber(values.cashAmount);
  const transfer = toNumber(values.transferAmount);
  const credit = toNumber(values.creditAmount);
  if (cash > 0) return "cash";
  if (transfer > 0) return "transfer";
  if (credit > 0) return "credit_card";
  return "cash";
}

export default function SellOutPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const http = useMemo(() => createHttpClient(), []);
  const incomingProduct = location?.state?.product || null;
  const stateProductId = useMemo(() => {
    const candidate =
      incomingProduct?.id ??
      incomingProduct?.documentId ??
      location?.state?.productId ??
      location?.state?.id;
    return String(candidate ?? "").trim();
  }, [incomingProduct, location?.state?.id, location?.state?.productId]);

  const { handleSubmit, register, watch, setValue } = useForm({
    defaultValues: {
      customerName: "",
      customerPhone: "",
      discount: "",
      deposit: "",
      cashAmount: "",
      transferAmount: "",
      creditAmount: "",
      price_others: "",
      tradeInPrice: "",
      tradeInImei: "",
      tradeInName: "",
      tradeInModel: "",
      tradeInBatteryMax: "",
      tradeInDeposit: "",
      note: "-",
    },
    mode: "onSubmit",
  });
  const values = watch();

  const [items, setItems] = useState(() => [makeEmptyItem(1)]);
  const [apiErr, setApiErr] = useState("");
  const [reportOpen, setReportOpen] = useState(false);
  const [reportUrl, setReportUrl] = useState("");
  const [saving, setSaving] = useState(false);
  const [phoneOptions, setPhoneOptions] = useState([]);
  const [accessoryOptions, setAccessoryOptions] = useState([]);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [tradeInModelOptions, setTradeInModelOptions] = useState([]);
  const [phoneLoading, setPhoneLoading] = useState(false);
  const [accessoryLoading, setAccessoryLoading] = useState(false);

  const scrollToField = useCallback((name) => {
    if (!name) return;
    const el = document.querySelector(`[name="${name}"]`);
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    if (typeof el.focus === "function") {
      el.focus({ preventScroll: true });
    }
  }, []);

  const scrollToSellItems = useCallback(() => {
    const el = document.getElementById("รายการขายออก");
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "center" });
  }, []);

  const onInvalidSubmit = useCallback((formErrors) => {
    const firstName = Object.keys(formErrors || {})[0];
    if (!firstName) return;

    const fieldLabelMap = {
      customerName: "ชื่อ-นามสกุลผู้ซื้อ",
      customerPhone: "เบอร์โทรศัพท์ผู้ซื้อ",
    };
    const messageFromRule = formErrors?.[firstName]?.message;
    const label = fieldLabelMap[firstName] || firstName;
    setApiErr(messageFromRule || `กรุณาตรวจสอบข้อมูลช่อง ${label}`);
    scrollToField(firstName);
  }, [scrollToField]);

  const fetchPhoneOptions = useCallback(async () => {
    try {
      setPhoneLoading(true);
      const res = await http.get("/products", {
        params: {
          "pagination[page]": 1,
          "pagination[pageSize]": 500,
          "populate[model][populate][category]": true,
          "populate[category]": true,
          "populate[capacity]": true,
          "populate[color]": true,
        },
      });
      const list = Array.isArray(res?.data?.data) ? res.data.data : [];
      const mapped = list
        .filter((p) => {
          const s = normalizeStatus(p?.product_status);
          return s === "available";
        })
        .map(mapPhoneOption)
        .filter((x) => x.optionId);
      setPhoneOptions(mapped);
    } catch (e) {
      console.error(e);
      setPhoneOptions([]);
    } finally {
      setPhoneLoading(false);
    }
  }, [http]);

  const fetchAccessoryOptions = useCallback(async () => {
    try {
      setAccessoryLoading(true);
      const res = await http.get("/product-accessories", {
        params: {
          "pagination[page]": 1,
          "pagination[pageSize]": 500,
          "populate[accessory_category]": true,
        },
      });
      const list = Array.isArray(res?.data?.data) ? res.data.data : [];
      const mapped = list
        .filter((a) => Number(a?.qty ?? 0) > 0)
        .map(mapAccessoryOption)
        .filter((x) => x.optionId);
      setAccessoryOptions(mapped);
    } catch (e) {
      console.error(e);
      setAccessoryOptions([]);
    } finally {
      setAccessoryLoading(false);
    }
  }, [http]);

  useEffect(() => {
    fetchPhoneOptions();
    fetchAccessoryOptions();
  }, []); 

  const fetchCategoryOptions = useCallback(async () => {
    try {
      const res = await http.get("/categories", {
        params: {
          "pagination[page]": 1,
          "pagination[pageSize]": 500,
        },
      });
      const list = Array.isArray(res?.data?.data) ? res.data.data : [];
      const mapped = list
        .map((it) => {
          const categoryId = String(it?.id ?? it?.documentId ?? "").trim();
          const name = asText(it?.name || it?.label || it?.title);
          return {
            value: categoryId || name,
            label: name || "-",
            categoryId,
          };
        })
        .filter((it) => String(it.value || "").trim() !== "");
      setCategoryOptions(mapped);
    } catch (e) {
      console.error(e);
      setCategoryOptions([]);
    }
  }, [http]);

  useEffect(() => {
    const incoming = location?.state?.product;
    if (!incoming) return;
    const mapped = mapPhoneOption(incoming);
    setItems((prev) => {
      if (!Array.isArray(prev) || prev.length === 0) return [makeEmptyItem(1)];
      const first = prev[0];
      return [
        {
          ...first,
          productType: "phone",
          productId: mapped.optionId,
          category: asText(incoming?.model?.category?.name || incoming?.category?.name || incoming?.product_name),
          imei: mapped.imei,
          name: mapped.name,
          cap: mapped.cap,
          color: mapped.color,
          warranty: mapped.warranty,
          warrantyInfo: mapped.warrantyInfo,
          warrantyDate: mapped.warrantyDate,
          batteryMax: mapped.batteryMax,
          price: mapped.price,
          pickerOpen: false,
          pickerQuery: "",
          raw: incoming,
        },
        ...prev.slice(1),
      ];
    });
  }, [location?.state?.product]);

  useEffect(() => {
    fetchCategoryOptions();
  }, []);

  const fetchTradeInModelOptions = useCallback(async (categoryValue) => {
    const selectedCategory = categoryOptions.find(
      (opt) => String(opt?.value || "").trim() === String(categoryValue || "").trim()
    );
    const categoryId = String(selectedCategory?.categoryId || selectedCategory?.value || "").trim();

    if (!categoryId) {
      setTradeInModelOptions([]);
      return;
    }

    try {
      const res = await http.get("/models", {
        params: {
          "filters[category][id][$eq]": categoryId,
          "pagination[page]": 1,
          "pagination[pageSize]": 500,
          sort: "sequence:asc",
        },
      });
      const list = Array.isArray(res?.data?.data) ? res.data.data : [];
      const mapped = list
        .map((it) => ({
          value: String(it?.id ?? it?.documentId ?? "").trim(),
          label: String(it?.name || "").trim(),
        }))
        .filter((it) => it.value && it.label);
      setTradeInModelOptions(mapped);
    } catch (e) {
      console.error(e);
      setTradeInModelOptions([]);
    }
  }, [categoryOptions, http]);

  useEffect(() => {
    fetchTradeInModelOptions(values.tradeInName);
  }, [fetchTradeInModelOptions, values.tradeInName]);

  const onPickProductType = (itemId, nextType) => {
    if (nextType === "phone" && !phoneOptions.length && !phoneLoading) fetchPhoneOptions();
    if (nextType === "accessory" && !accessoryOptions.length && !accessoryLoading) fetchAccessoryOptions();

    setItems((prev) =>
      prev.map((it) =>
        it.id === itemId
          ? {
            ...it,
            productType: nextType,
            productId: "",
            category: "",
            imei: "",
            pickerOpen: !!nextType,
            pickerQuery: "",
            name: "",
            cap: "",
            color: "",
            warranty: "",
            warrantyInfo: "",
            warrantyDate: "",
            batteryMax: "",
            price: 0,
          }
          : it
      )
    );
  };

  const onTogglePicker = (itemId) => {
    setItems((prev) =>
      prev.map((it) => (it.id === itemId ? { ...it, pickerOpen: !it.pickerOpen } : it))
    );
  };

  const onChangePickerQuery = (itemId, nextQuery) => {
    setItems((prev) =>
      prev.map((it) => (it.id === itemId ? { ...it, pickerQuery: nextQuery } : it))
    );
  };

  const onPickProduct = (itemId, option) => {
    setItems((prev) =>
      prev.map((it) =>
        it.id === itemId
          ? {
            ...it,
            productId: option?.optionId || "",
            category: option?.category || "",
            imei: option?.imei || "",
            name: option?.name ?? "",
            cap: option?.cap ?? "",
            color: option?.color ?? "",
            warranty: option?.warranty ?? "",
            warrantyInfo: option?.warrantyInfo ?? "",
            warrantyDate: option?.warrantyDate ?? "",
            batteryMax: option?.batteryMax ?? "",
            price: Number(option?.price ?? 0),
            pickerOpen: false,
            pickerQuery: "",
            raw: option?.raw || null,
          }
          : it
      )
    );
  };

  const addItem = () => setItems((prev) => [...prev, makeEmptyItem(prev.length + 1)]);
  const removeItem = (id) => setItems((prev) => prev.filter((x) => x.id !== id));

  // totals (อ่านจาก react-hook-form ด้วย register -> ใช้ FormData ตอน submit)
  const subtotal = useMemo(
    () => items.reduce((sum, it) => sum + Number(it.price || 0), 0),
    [items]
  );

  const discountAmount = toNumber(values.discount);
  const depositAmount = toNumber(values.deposit);
  const cashAmount = toNumber(values.cashAmount);
  const transferAmount = toNumber(values.transferAmount);
  const creditAmount = toNumber(values.creditAmount);
  const otherAmount = toNumber(values.price_others);
  const tradeInAmount = toNumber(values.tradeInPrice);
  const totalPaidAmount = depositAmount + cashAmount + transferAmount + creditAmount + otherAmount;
  const rawRemainingBalance = -subtotal + discountAmount + totalPaidAmount + tradeInAmount;
  const remainingBalance = Math.min(0, rawRemainingBalance);

  const onSubmit = async (formValues) => {
    try {
      setSaving(true);
      setApiErr("");

      const sanitizedPhone = String(formValues.customerPhone || "")
        .replace(/\D/g, "")
        .slice(0, 10);
      if (!/^\d{10}$/.test(sanitizedPhone)) {
        scrollToField("customerPhone");
        throw new Error("เบอร์โทรศัพท์ของผู้ซื้อต้องเป็นตัวเลข 10 หลัก");
      }

      const normalizedItems = Array.isArray(items)
        ? items.filter(
          (it) =>
            it?.productId &&
            (it?.productType === "phone" || it?.productType === "accessory")
        )
        : [];
      if (!normalizedItems.length) {
        scrollToSellItems();
        throw new Error("กรุณาเลือกรายการขายออกอย่างน้อย 1 รายการ");
      }

      const primaryProductId = stateProductId;
      if (!primaryProductId) {
        throw new Error("ไม่พบ product_id จากข้อมูลหน้าก่อนหน้า กรุณาเลือกสินค้าจากสต็อกอีกครั้ง");
      }

      const phoneIds = normalizedItems
        .filter((it) => it.productType === "phone")
        .map((it) => String(it.productId))
        .filter((productId) => productId && productId !== primaryProductId);

      const uniquePhoneIds = [...new Set(phoneIds)];

      const accessoryPayload = normalizedItems
        .filter((it) => it.productType === "accessory")
        .map((it) => ({
          id: String(it.productId),
          qty: 1,
        }));

      const tradeInImei = String(formValues.tradeInImei || "").trim().toUpperCase();
      const tradeInCategoryValue = String(formValues.tradeInName || "").trim();
      const tradeInModelValue = String(formValues.tradeInModel || "").trim();
      const tradeInBatteryMaxValue = String(formValues.tradeInBatteryMax ?? "").trim();
      const tradeInPriceValue = toNumber(formValues.tradeInPrice);
      const selectedTradeInCategory = categoryOptions.find(
        (opt) => String(opt?.value || "").trim() === tradeInCategoryValue
      );
      const selectedTradeInModel = tradeInModelOptions.find(
        (opt) => String(opt?.value || "").trim() === tradeInModelValue
      );
      const tradeInCategoryName = asText(selectedTradeInCategory?.label || tradeInCategoryValue).trim();
      const tradeInModelName = asText(selectedTradeInModel?.label).trim();
      const tradeInName =
        (tradeInCategoryName && tradeInModelName
          ? `${tradeInCategoryName} ${tradeInModelName}`
          : tradeInCategoryName || tradeInModelName || "Trade-in").trim();
      const hasTradeInData = Boolean(tradeInImei || tradeInCategoryValue);
      if (hasTradeInData && (!tradeInImei || !tradeInCategoryValue)) {
        scrollToField(!tradeInImei ? "tradeInImei" : "tradeInName");
        throw new Error("กรุณากรอกข้อมูล Trade-in ให้ครบ (IMEI และชื่อสินค้า)");
      }

      const payload = {
        data: {
          product_id: primaryProductId,
          product_additional: uniquePhoneIds,
          additional_accessories: accessoryPayload,
          payment_method: resolvePaymentMethod(formValues),
          price_cash: toNumber(formValues.cashAmount),
          price_transfer: toNumber(formValues.transferAmount),
          price_card: toNumber(formValues.creditAmount),
          price_others: toNumber(formValues.price_others),
          customer_name: String(formValues.customerName || "").trim(),
          customer_phone: sanitizedPhone,
          note: String(formValues.note || "").trim(),
          date: new Date().toISOString(),
          trade_discount: toNumber(formValues.discount),
          deposit: toNumber(formValues.deposit),
          ...(hasTradeInData
            ? {
              trade_in_imei: tradeInImei,
              trade_in_name: tradeInName,
              trade_in_model: tradeInModelValue,
              trade_in_battery_health: tradeInBatteryMaxValue,
              trade_in_amount: tradeInPriceValue,
            }
            : {}),
        },
      };

      const sellRes = await http.post("/products/sell", payload);
      const reportUrlFromSellResponse = extractSellReportPdfUrlFromResponse(sellRes?.data);
      await delay(3000);
      const refreshedReportUrl = await fetchProductReportPdfUrlById(http, primaryProductId);
      const latestReportUrl = refreshedReportUrl || reportUrlFromSellResponse;

      if (hasTradeInData) {
        const tradeInProductData = {
          imei: tradeInImei,
          product_name: tradeInName,
          product_status: "draft",
        };

        if (tradeInBatteryMaxValue !== "") {
          tradeInProductData.battery_health = toNumber(tradeInBatteryMaxValue);
        }

        const categoryId = String(
          selectedTradeInCategory?.categoryId || selectedTradeInCategory?.value || tradeInCategoryValue
        ).trim();
        if (categoryId) {
          tradeInProductData.category = categoryId;
        }

        // await http.post("/products", { data: tradeInProductData });
      }

      if (latestReportUrl) {
        setReportUrl(latestReportUrl);
        setReportOpen(true);
      } else {
        navigate(-1);
      }
    } catch (err) {
      console.error(err);
      const msg = err?.message;
      setApiErr(msg || "บันทึกขายออกไม่สำเร็จ กรุณาลองใหม่อีกครั้ง");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4 bg-[#F5F5F5] min-h-full">
      <ReportPreviewModal
        open={reportOpen}
        url={reportUrl}
        onClose={() => {
          setReportOpen(false);
          navigate(-1);
        }}
      />

      {/* breadcrumb + title */}
      <div className="space-y-2 p-4">
        <div className="flex items-center gap-2 text-sm text-slate-500">
          <Devices size={16} className="h-4 w-4" />
          <span className="hover:underline cursor-pointer" onClick={() => navigate(-1)}>ซื้อขายหลัก</span>
          <span className="text-slate-300">/</span>
          <span className="font-semibold text-[#047DED]">ขายออก</span>
        </div>

        <div className="flex items-end justify-between gap-3">
          <div>
            <div className="flex items-center gap-2 text-4xl font-semibold text-slate-[#1D1616]">
              <span>ขายสินค้าออก</span>
              <ArrowRight size={22} className="text-slate-700" />
            </div>
            <div className="mt-1 text-sm text-slate-500">
              ขายสินค้าออกจากสต๊อกสำหรับทุกสินค้า
            </div>
          </div>
        </div>
      </div>

      <div className="rounded-xl bg-white shadow-md m-4">
        {/* row: title + buttons */}
        <div className="flex items-center justify-between gap-3 px-6 py-3 rounded-xl">
          <div className="text-lg font-semibold text-slate-[#1D1616]">บันทึกรายการ</div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="h-9 rounded-lg border border-slate-200 bg-white px-4 text-sm font-bold text-slate-700 hover:bg-slate-50 active:scale-[0.99]"
            >
              ยกเลิก
            </button>

            <button
              type="button"
              onClick={handleSubmit(onSubmit, onInvalidSubmit)}
              disabled={saving}
              className={classNames(
                "h-9 rounded-lg px-4 text-sm font-bold text-white hover:opacity-95 active:scale-[0.99]",
                saving ? "bg-slate-400" : "bg-[#111]"
              )}
            >
              {saving ? "กำลังบันทึก..." : "บันทึกขายออก"}
            </button>
          </div>
        </div>

        {/* divider */}
        <div className="h-px bg-slate-200 mx-6" />

        {/* row: total */}
        <div className="flex items-center justify-between px-6 py-5">
          <div className="text-sm text-[#1D1616] font-semibold">ยอดที่ต้องชำระ</div>
          <div className="text-sm font-semibold text-[#047DED]">
            {Money(subtotal)}
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit(onSubmit, onInvalidSubmit)} className="space-y-4 bg-white rounded-xl">
        {apiErr && (
          <div className="mx-4 rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
            {apiErr}
          </div>
        )}
        {/* action bar */}
        {/* action bar (ปรับให้ตรง UI ตามรูป) */}


        {/* รายการขายออก */}
        <SectionCard
          title="รายการขายออก"
          right={
            <div className="text-xs font-bold text-[#047DED]">
              {items.length > 0 ? "" : ""}
            </div>
          }
        // icon={Package}
        >
          <div className="space-y-4">
            {items.map((it, idx) => {
              const hasSelected = !!it.productId;
              const cardTitle = `รายการที่ ${idx + 1}`;

              return (
                <div key={it.id} className="rounded-xl border border-slate-200 bg-white">
                  <div className="flex items-center justify-between px-4 py-3">
                    <div className="flex items-center gap-2 text-sm font-semibold text-slate-[#1D1616]">
                      <Package size={16} className="text-slate-700" />
                      {cardTitle}
                    </div>

                    {items.length > 1 ? (
                      <button
                        type="button"
                        onClick={() => removeItem(it.id)}
                        className="inline-flex items-center gap-2 rounded-lg px-3 py-2 text-xs font-bold text-red-600 hover:bg-red-50"
                      >
                        ลบรายการ
                        <Trash2 size={14} />
                      </button>
                    ) : null}
                  </div>

                  {/* content */}
                  <div className="px-4 pb-4">
                    {hasSelected ? (
                      // ✅ แบบโชว์ค่าจริง (เหมือนรายการที่ 1 ในรูป)
                      <div className="rounded-xl bg-[#F3F4F6] p-4">
                        <div className="space-y-3">
                          <div className="border-b border-slate-200 pb-3">
                            <div className="text-xs text-slate-500">ชื่อสินค้า</div>
                            <div className="mt-1 text-sm font-bold text-slate-[#1D1616]">{it.name}</div>
                          </div>

                          <div className="border-b border-slate-200 pb-3">
                            <div className="text-xs text-slate-500">ราคา</div>
                            <div className="mt-1 text-sm font-bold text-slate-[#1D1616]">
                              {Money(it.price)}
                            </div>
                          </div>

                          <div className="border-b border-slate-200 pb-3">
                            <div className="text-xs text-slate-500">ความจุ</div>
                            <div className="mt-1 text-sm font-bold text-slate-[#1D1616]">{it.cap}</div>
                          </div>

                          <div className="border-b border-slate-200 pb-3">
                            <div className="text-xs text-slate-500">สี</div>
                            <div className="mt-1 text-sm font-bold text-slate-[#1D1616]">{it.color}</div>
                          </div>

                          <div className="border-b border-slate-200 pb-3">
                            <div className="text-xs text-slate-500">ประกัน:</div>
                            <div className="mt-1 text-sm font-bold text-slate-[#1D1616]">{it.warrantyInfo || it.warranty || "-"}</div>
                          </div>

                          {String(it.warrantyInfo || "").trim() === "ประกันศูนย์" && (
                            <div className="border-b border-slate-200 pb-3">
                              <div className="text-xs text-slate-500">วันหมดอายุประกัน:</div>
                              <div className="mt-1 text-sm font-bold text-slate-[#1D1616]">{formatDateDisplay(it.warrantyDate)}</div>
                            </div>
                          )}

                          <div className="border-b border-slate-200 pb-0">
                            <div className="text-xs text-slate-500">ความจุแบตเตอรี่:</div>
                            <div className="mt-1 text-sm font-bold text-slate-[#1D1616]">
                              {String(it.batteryMax)}%
                            </div>
                          </div>
                        </div>
                      </div>
                    ) : (
                      // ✅ แบบเลือกข้อมูล (เหมือนรายการที่ 2 ในรูป)
                      <div className="grid grid-cols-1 gap-2">
                        <UnderlineSelect
                          label="ประเภทสินค้า"
                          value={it.productType}
                          onChange={(v) => onPickProductType(it.id, v)}
                          options={PRODUCT_TYPES}
                          placeholder="เลือกข้อมูล"
                        />
                        <ProductPickerPanel
                          type={it.productType}
                          open={!!it.pickerOpen}
                          loading={it.productType === "phone" ? phoneLoading : accessoryLoading}
                          query={it.pickerQuery || ""}
                          rows={(it.productType === "phone" ? phoneOptions : accessoryOptions).filter((row) => {
                            const selectedInOtherRows = new Set(
                              items
                                .filter(
                                  (picked) =>
                                    picked.id !== it.id &&
                                    picked.productType === it.productType &&
                                    String(picked.productId || "").trim() !== ""
                                )
                                .map((picked) => String(picked.productId))
                            );
                            if (selectedInOtherRows.has(String(row?.optionId || ""))) return false;

                            const q = String(it.pickerQuery || "").trim().toLowerCase();
                            if (!q) return true;
                            const haystack = [
                              row?.name,
                              row?.color,
                              row?.imei,
                              row?.category,
                              row?.cap,
                              row?.price,
                            ]
                              .map((x) => String(x ?? "").toLowerCase())
                              .join(" ");
                            return haystack.includes(q);
                          })}
                          onToggle={() => onTogglePicker(it.id)}
                          onQueryChange={(v) => onChangePickerQuery(it.id, v)}
                          onSelect={(row) => onPickProduct(it.id, row)}
                        />
                      </div>
                    )}
                  </div>
                </div>
              );
            })}

            {/* add */}
            <button
              type="button"
              onClick={addItem}
              className="w-full rounded-xl bg-[#047DED1A] py-3 text-sm font-semibold text-[#047DED] hover:opacity-90 active:scale-[0.99]"
            >
              เพิ่มรายการ
            </button>
          </div>
        </SectionCard>

        <SectionCard title="ช่องทางการติดต่อ">
          <UnderlineInput
            name="customerName"
            label="ชื่อลูกค้า:"
            placeholder="กรอกข้อมูล"
            value={values.customerName}
            onChange={(v) => setValue("customerName", v)}
          />
          <input
            className="hidden"
            {...register("customerName", {
              required: "กรุณากรอกชื่อ-นามสกุลผู้ซื้อ",
            })}
            defaultValue=""
          />

          <UnderlineInput
            name="customerPhone"
            label="เบอร์โทรศัพท์ของผู้ซื้อ:"
            placeholder="กรอกข้อมูล"
            value={values.customerPhone}
            onChange={(v) =>
              setValue(
                "customerPhone",
                String(v || "")
                  .replace(/\D/g, "")
                  .slice(0, 10)
              )
            }
          />
          <input
            className="hidden"
            {...register("customerPhone", {
              required: "กรุณากรอกเบอร์โทรศัพท์ผู้ซื้อ",
              validate: (value) => {
                const sanitized = String(value || "").replace(/\D/g, "");
                return /^\d{10}$/.test(sanitized) || "เบอร์โทรศัพท์ของผู้ซื้อต้องเป็นตัวเลข 10 หลัก";
              },
            })}
            defaultValue=""
          />

          <UnderlineInput
            label="ส่วนลดสินค้า (บาท):"
            placeholder="0.00"
            type="number"
            value={values.discount}
            onChange={(v) => setValue("discount", v)}
          />
          <input className="hidden" {...register("discount")} defaultValue="0" />
        </SectionCard>

        <SectionCard title="ช่องทางการรับเงิน">
          <div className="rounded-xl border border-slate-200 bg-white flex flex-col p-4">
            <div className="flex items-center gap-2 mb-4 text-sm font-semibold text-slate-[#1D1616]">
              <Card size={16} className="text-slate-700" />
              ช่องทางการรับเงิน
            </div>
            <div className="space-y-1">
              <UnderlineInput
                label="มัดจำ"
                placeholder="฿0.00"
                type="number"
                value={values.deposit}
                onChange={(v) => setValue("deposit", v)}
              />
              <input className="hidden" {...register("deposit")} defaultValue="0" />

              <UnderlineInput
                label="เงินสด"
                placeholder="฿0.00"
                type="number"
                value={values.cashAmount}
                onChange={(v) => setValue("cashAmount", v)}
              />
              <input className="hidden" {...register("cashAmount")} defaultValue="0" />

              <UnderlineInput
                label="เงินโอน"
                placeholder="฿0.00"
                type="number"
                value={values.transferAmount}
                onChange={(v) => setValue("transferAmount", v)}
              />
              <input className="hidden" {...register("transferAmount")} defaultValue="0" />

              <UnderlineInput
                label="บัตรเครดิต"
                placeholder="฿0.00"
                type="number"
                value={values.creditAmount}
                onChange={(v) => setValue("creditAmount", v)}
              />
              <input className="hidden" {...register("creditAmount")} defaultValue="0" />

              <UnderlineInput
                label="อื่นๆ"
                placeholder="฿0.00"
                type="text"
                value={values.price_others}
                onChange={(v) => setValue("price_others", v)}
              />
              <input className="hidden" {...register("price_others")} defaultValue="0" />
            </div>

            <div className="mt-4 flex items-center justify-between border-t pt-4">
              <div className="text-sm font-bold text-slate-[#1D1616]">คงเหลือ</div>
              <div className={classNames(
                "text-sm font-semibold",
                remainingBalance < 0 ? "text-red-600" : "text-[#047DED]"
              )}>
                {Money(remainingBalance)}
              </div>
            </div>
          </div>
        </SectionCard>
        {/* Trade-in */}
        <SectionCard title="Trade-in">
          <div className="rounded-xl border border-slate-200 bg-white flex flex-col p-4">
            <div className="flex items-center gap-2 text-sm font-semibold text-slate-[#1D1616]">
              <Box size={16} className="text-slate-700" />
              รายการ Trade-in
            </div>
            <UnderlineInput
              label="Trade-in (บาท):"
              placeholder="฿0.00"
              type="number"
              value={values.tradeInPrice}
              onChange={(v) => setValue("tradeInPrice", v)}
            />
            <input className="hidden" {...register("tradeInPrice")} defaultValue="0" />

            <UnderlineInput
              name="tradeInImei"
              label="Trade-in IMEI:"
              placeholder="กรอกข้อมูล"
              value={values.tradeInImei}
              maxLength={15}
              onChange={(v) => setValue("tradeInImei", v)}
              forceUppercase
              forceAlphanumeric
            />
            <input className="hidden" {...register("tradeInImei")} defaultValue="" />

            <UnderlineSelect
              name="tradeInName"
              label="ชื่อสินค้า Trade-in:"
              placeholder="เลือกหมวดหมู่สินค้า"
              value={values.tradeInName}
              onChange={(v) => {
                setValue("tradeInName", v);
                setValue("tradeInModel", "");
              }}
              options={categoryOptions}
            />
            <input className="hidden" {...register("tradeInName")} defaultValue="" />

            <UnderlineSelect
              name="tradeInModel"
              label="รุ่นสินค้า Trade-in:"
              placeholder="เลือกรุ่นสินค้า"
              value={values.tradeInModel}
              onChange={(v) => setValue("tradeInModel", v)}
              options={tradeInModelOptions}
            />
            <input className="hidden" {...register("tradeInModel")} defaultValue="" />

            <UnderlineInput
              label="ความจุแบตเตอรี่สูงสุดของสินค้า Trade-in:"
              placeholder="%00"
              type="number"
              value={values.tradeInBatteryMax}
              onChange={(v) => setValue("tradeInBatteryMax", v)}
            />
            <input className="hidden" {...register("tradeInBatteryMax")} defaultValue="0" />
            {/* <UnderlineInput
              label="มัดจำ"
              placeholder="฿0.00"
              type="number"
              value={values.tradeInDeposit}
              onChange={(v) => setValue("tradeInDeposit", v)}
            /> */}
            <input className="hidden" {...register("tradeInDeposit")} defaultValue="0" />
          </div>
        </SectionCard>

        {/* เพิ่มเติม */}
        <SectionCard title="เพิ่มเติม">
          <div className="rounded-xl border border-slate-200 bg-white flex flex-col p-4">
            <div className="flex items-center gap-2 text-sm font-semibold text-slate-[#1D1616]">
              เพิ่มเติม
            </div>
            <div className="py-3">
              <UnderlineInput
                label="หมายเหตุ"
                placeholder=""
                type="text"
                value={values.note}
                onChange={(v) => setValue("note", v)}
              />
              <input className="hidden" {...register("note")} defaultValue="-" />
            </div>
          </div>
        </SectionCard>
      </form>
    </div>
  );
}
